<script setup>
import { nextTick } from 'vue'
import { userAuthState } from '@/@core/auth'
import FilterComponent from '@/@core/components/custom/FilterComponent.vue'
import AnalyticsTicketTracker from '@/@core/components/service-desk/AnalyticsTicketTracker.vue'
import { useTicketAlerts } from '@/composables/useTicketAlerts'
import { useToast } from '@/composables/useToast'
import ticketService from '@/services/service-desk/ticket.service'
import { $api } from '@/utils/api'
import CustomerCommunicationDrawer from '@/views/service-desk/customer-communication/Drawer.vue'
import CustomerCommunicationLogDrawer from '@/views/service-desk/customer-communication/LogDrawer.vue'
import TicketDrawer from '@/views/service-desk/drawer/TicketDrawer.vue'
import TicketFormDrawer from '@/views/service-desk/drawer/TicketFormDrawer.vue'
import TicketFilterSheet from '@/views/service-desk/ticket-management/TicketFilterSheet.vue'
import TicketTable from '@/views/service-desk/ticket-management/TicketTable.vue'

definePage({
  meta: {
    name: 'ticket-management',
    subject: 'service_desk.menu',
    action: 'ticket_management',
  },
})

const route = useRoute()
const router = useRouter()

const ticketFilters = ref()

const { isCompact } = useTicketListDisplay()

const {
  data: filterOptions,
} = await useApi(createUrl('/service-desk/tickets/filters', {
  query: {
    type: 'service-desk',
  },
}))

const isTicketDrawerVisible = ref(false)
const ticketDrawerId = ref(null)
const refTicketTable = ref()
const refTicketDrawer = ref()
const communicationDrawerData = ref()
const isFilterSheetOpen = ref(false)

const showTicket = feedbackData => {
  ticketDrawerId.value = feedbackData?.id
  isTicketDrawerVisible.value = true
}

// Statuses a ticket may be accepted FROM, as the API serialises them -- these
// are enum VALUES, not keys. getStatusAttribute returns a BenSampo enum that
// serialises to its value, which is why the drawer compares against 'Resolved'.
// Written in key style this gate would never pass and Accept would silently
// never fire.
const ACCEPTABLE_STATUSES = ['Pending', 'Re-Assign', 'Re-Open', 'Unattended']

const runAcceptChain = async ticketId => {
  try {
    const { data: ticket } = await $api(`/service-desk/tickets/${ticketId}`)

    const auth = userAuthState()

    // The ticket may have moved on between the alert firing and the tap.
    if (Number(ticket?.assigned_user_id) !== Number(auth?.user?.id)) return
    if (!ACCEPTABLE_STATUSES.includes(ticket?.status)) return

    await ticketService.acceptTicket(ticketId)

    // Serial on purpose: the drawer fetches independently and is
    // last-write-wins, so a parallel refresh can repaint the pre-accept status.
    // Refreshing through the exposed method rather than toggling the id keeps
    // the drawer mounted -- the id toggle would replay its entrance animation
    // on every accept.
    await nextTick()
    refTicketDrawer.value?.refresh()
    refTicketTable.value?.fetchTickets()
  } catch (error) {
    // $api already toasts 4xx/5xx and flags them as reported. Only speak up if
    // it stayed silent, and never claim success -- nothing displayed
    // "accepted", so an honest failure just leaves the true status showing.
    if (!error?.reported) useToast().error('Could not mark the ticket as accepted')
  }
}

// A watcher, not a one-shot check: the alert host and the service worker both
// open a ticket by pushing ?ticket=<id> onto this route, and the page is
// already mounted when they do.
watch(
  () => [route.query.ticket, route.query.accept],
  ([ticketId, accept]) => {
    if (!ticketId) return

    ticketDrawerId.value = ticketId
    isTicketDrawerVisible.value = true

    if (accept) runAcceptChain(ticketId)
  },
  { immediate: true },
)

// A ticket the user was just assigned is not in the table they are looking at.
// Refetch rather than splicing the alert payload into the rows: that payload is
// flat scalars and carries no status, turn-around time, escalation level,
// category or unit, and this table is server-filtered and server-paginated -- an
// injected row would ignore the active filters, desync meta.total, and vanish on
// the next sort or page change.
const { alertSeq } = useTicketAlerts()

watch(alertSeq, () => refTicketTable.value?.fetchTickets())

// Clearing the query on close matters: without it the watcher never refires for
// the same ticket, so accepting an alert for a ticket you just closed does
// nothing -- router.push to an identical query is a no-op.
watch(isTicketDrawerVisible, open => {
  if (!open && route.query.ticket) {
    router.replace({ query: { ...route.query, ticket: undefined, accept: undefined } })
  }
})

const isCommunicationDrawerVisible = ref(false)

const showCommunicationDrawer= (feedbackData, type, communicationFor='') => {
  communicationDrawerData.value = feedbackData
  communicationDrawerData.value.communicationFor = communicationFor
  isCommunicationDrawerVisible.value = true
}

const isCommunicationLogDrawerVisible = ref(false)

const showCommunicationLogDrawer = (feedbackData, type) => {
  communicationDrawerData.value = feedbackData
  isCommunicationLogDrawerVisible.value = true
}

const refTicketTracker =ref(null)

const refreshTicketData = async (closeDrawer = true) => {
  if (closeDrawer) isTicketDrawerVisible.value = false
  refTicketTable.value?.fetchTickets()
  refTicketTracker.value?.fetchStatusGraph()
}

const isCreateTicketDrawerVisible = ref(false)

const onUpdateTableFilter = data => {
  ticketFilters.value = {
    ...ticketFilters.value,
    ...data,
  }
}

// Below 1100px the inline FilterComponent never mounts, so nothing emits the
// server-supplied defaults (the six open statuses + last month's date range).
// Without this, compact users would see ALL statuses across ALL time while
// desktop users see the filtered default set — and the first "Show results"
// would visibly jump the list. Mirrors FilterComponent's initializeDefaults.
const seedDefaultFilters = () => {
  const seeded = {}

  filterOptions.value?.forEach(filter => {
    if (!filter.default) return

    if (filter.type === 'daterange') seeded[filter.key] = [filter.default.start, filter.default.end]
    else seeded[filter.key] = filter.default
  })

  return seeded
}

watch([filterOptions, isCompact], () => {
  if (!isCompact.value) return
  if (ticketFilters.value !== undefined) return

  ticketFilters.value = seedDefaultFilters()
}, { immediate: true })
</script>

<template>
  <section>
    <VRow
      v-if="!isCompact"
      class="match-height mb-4"
    >
      <VCol
        cols="12"
        md="6"
        lg="6"
      >
        <FilterComponent
          v-model:filters="ticketFilters"
          :filters-options="filterOptions"
          :initial-filters="ticketFilters"
          :style="{ cardVariant: 'flat', cardClass: 'border' }"
        />
      </VCol>
      <VCol
        cols="12"
        md="6"
        lg="6"
      >
        <AnalyticsTicketTracker
          ref="refTicketTracker"
          :ticket-filters="ticketFilters"
          :style="{ cardVariant: 'flat', cardClass: 'border' }"
          page="ticket-management"
        />
      </VCol>
    </VRow>

    <TicketTable
      ref="refTicketTable"
      :ticket-filters="ticketFilters"
      page="ticket-management"
      :card-on-compact="true"
      @show-ticket="showTicket"
      @show-communication-drawer="showCommunicationDrawer"
      @show-communication-log-drawer="showCommunicationLogDrawer"
      @show-ticket-create-drawer="() => {
        isCreateTicketDrawerVisible = true
      }"
      @open-filters="isFilterSheetOpen = true"
      @update:filter="onUpdateTableFilter"
    />

    <TicketFilterSheet
      v-if="isCompact"
      v-model="isFilterSheetOpen"
      :filters-options="filterOptions"
      :applied-filters="ticketFilters ?? {}"
      @apply="ticketFilters = $event"
    />

    <CustomerCommunicationDrawer
      v-model:isDrawerOpen="isCommunicationDrawerVisible"
      :customer="communicationDrawerData?.customer"
      notification-action="ticket_customer_communication"
    />

    <CustomerCommunicationLogDrawer
      v-model:isDrawerOpen="isCommunicationLogDrawerVisible"
      :customer="communicationDrawerData?.customer"
      notification-action="ticket_customer_communication"
    />

    <TicketDrawer
      ref="refTicketDrawer"
      v-model:isDrawerOpen="isTicketDrawerVisible"
      :ticket-id="ticketDrawerId"
      @refresh-ticket-data="refreshTicketData"
    />

    <TicketFormDrawer
      v-model:isDrawerOpen="isCreateTicketDrawerVisible"
      :form-data="null"
      @refetch-listing="refreshTicketData(false)"
    />
  </section>
</template>
